// src/pages/people/biller.jsx
import { React, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Edit, Trash2 } from "react-feather";
import Table from "../../core/pagination/datatable";
import TooltipIcons from "../../core/common/tooltip-content/tooltipIcons";
import RefreshIcon from "../../core/common/tooltip-content/refresh";
import CollapesIcon from "../../core/common/tooltip-content/collapes";
import CommonFooter from "../../core/common/footer/commonFooter";
import {
  createBiller,
  getAllBillers,
  updateBiller,
  deleteBillerApi,
} from "../../api/billerApi";
import { uploadCatalogImage } from "../../api/productApi"; // ✅ reuse uploader
import { toast } from "react-toastify";

const Biller = () => {
  // ---------- Add form ----------
  const [billerFirstNameEN, setBillerFirstNameEN] = useState("");
  const [billerFirstNameAR, setBillerFirstNameAR] = useState("");
  const [billerLastNameEN, setBillerLastNameEN] = useState("");
  const [billerLastNameAR, setBillerLastNameAR] = useState("");
  const [billerCompanyNameEN, setBillerCompanyNameEN] = useState("");
  const [billerCompanyNameAR, setBillerCompanyNameAR] = useState("");
  const [billerEmail, setBillerEmail] = useState("");
  const [billerPhone, setBillerPhone] = useState("");
  const [billerPhone2, setBillerPhone2] = useState("");
  const [billerAddressEN, setBillerAddressEN] = useState("");
  const [billerAddressAR, setBillerAddressAR] = useState("");
  const [billerCityEN, setBillerCityEN] = useState("");
  const [billerCityAR, setBillerCityAR] = useState("");
  const [billerStateEN, setBillerStateEN] = useState("");
  const [billerStateAR, setBillerStateAR] = useState("");
  const [billerCountryEN, setBillerCountryEN] = useState("");
  const [billerCountryAR, setBillerCountryAR] = useState("");
  const [billerPostalCode, setBillerPostalCode] = useState("");
  const [billerStatus, setBillerStatus] = useState(true);

  // ✅ image (Add): preview URL + key-only for DB
  const [billerImagePreview, setBillerImagePreview] = useState("");
  const [billerImageKey, setBillerImageKey] = useState("");

  // ---------- List / table ----------
  const [billers, setBillers] = useState([]);

  // ---------- Edit modal ----------
  const [editBillerId, setEditBillerId] = useState(null);
  const [editFirstNameEN, setEditFirstNameEN] = useState("");
  const [editFirstNameAR, setEditFirstNameAR] = useState("");
  const [editLastNameEN, setEditLastNameEN] = useState("");
  const [editLastNameAR, setEditLastNameAR] = useState("");
  const [editCompanyNameEN, setEditCompanyNameEN] = useState("");
  const [editCompanyNameAR, setEditCompanyNameAR] = useState("");
  const [editEmail, setEditEmail] = useState("");
  const [editPhone, setEditPhone] = useState("");
  const [editPhone2, setEditPhone2] = useState("");
  const [editAddressEN, setEditAddressEN] = useState("");
  const [editAddressAR, setEditAddressAR] = useState("");
  const [editCityEN, setEditCityEN] = useState("");
  const [editCityAR, setEditCityAR] = useState("");
  const [editStateEN, setEditStateEN] = useState("");
  const [editStateAR, setEditStateAR] = useState("");
  const [editCountryEN, setEditCountryEN] = useState("");
  const [editCountryAR, setEditCountryAR] = useState("");
  const [editPostalCode, setEditPostalCode] = useState("");
  const [editStatus, setEditStatus] = useState(true);

  // ✅ image (Edit): preview URL + key-only
  const [editImagePreview, setEditImagePreview] = useState("");
  const [editImageKey, setEditImageKey] = useState("");

  // ---------- Delete ----------
  const [deleteBillerId, setDeleteBillerId] = useState(null);

  // ---------------------------------------------------------------------------
  // Helpers (same semantics as Category/Customers)
  // ---------------------------------------------------------------------------
  const normalizeKey = (s) => {
    if (!s) return "";
    let v = String(s).trim();
    v = v.replace(/^\/+/, "");
    v = v.replace(/^catelog\//i, "catalog/");
    if (!/^catalog\//i.test(v)) {
      v = `catalog/${v.split("/").pop()}`;
    }
    return v;
  };

  const toPublicUrl = (v) => {
    if (!v) return "";
    if (/^https?:\/\//i.test(v)) return v;

    const key = normalizeKey(v);
    const base = (process.env.REACT_APP_API_IMAGE_BASE_URL ||
      "https://hamilton-shop-images.s3.ap-south-1.amazonaws.com").replace(/\/+$/, "");
    return `${base}/${key}`;
  };

  const buildBillerFileName = (file, firstEn, companyEn, emailVal) => {
    const ext =
      (file.type && file.type.split("/")[1]) ||
      file.name.split(".").pop() ||
      "jpg";
    const stamp = new Date().toISOString().replace(/[-:.TZ]/g, "").slice(0, 14);
    const base = (companyEn || firstEn || emailVal || "biller")
      .toString()
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9-_]/g, "");
    // e.g., BILLER_hamilton-services_20251112094500.jpg
    return `BILLER_${base || "biller"}_${stamp}.${ext}`;
  };

  // ---------------------------------------------------------------------------
  // Image uploads
  // ---------------------------------------------------------------------------
  const handleAddImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = async () => {
      const dataUrl = reader.result;
      setBillerImagePreview(dataUrl); // instant preview

      try {
        const desiredName = buildBillerFileName(
          file,
          billerFirstNameEN,
          billerCompanyNameEN,
          billerEmail
        );
        const { key } = await uploadCatalogImage(dataUrl, desiredName);
        const k = normalizeKey(key);
        setBillerImageKey(k);
        setBillerImagePreview(toPublicUrl(k)); // swap to public S3 URL
      } catch (err) {
        console.error(err);
        toast.error(window.translate("Image upload failed"));
        setBillerImagePreview("");
        setBillerImageKey("");
      }
    };
    reader.readAsDataURL(file);
  };

  const handleEditImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = async () => {
      const dataUrl = reader.result;
      setEditImagePreview(dataUrl);

      try {
        const desiredName = buildBillerFileName(
          file,
          editFirstNameEN,
          editCompanyNameEN,
          editEmail
        );
        const { key } = await uploadCatalogImage(dataUrl, desiredName);
        const k = normalizeKey(key);
        setEditImageKey(k);
        setEditImagePreview(toPublicUrl(k));
      } catch (err) {
        console.error(err);
        toast.error(window.translate("Image upload failed"));
      }
    };
    reader.readAsDataURL(file);
  };

  // ---------------------------------------------------------------------------
  // CRUD
  // ---------------------------------------------------------------------------
  const handleCreateBiller = async () => {
    if (
      !billerFirstNameEN ||
      !billerFirstNameAR ||
      !billerLastNameEN ||
      !billerLastNameAR ||
      !billerCompanyNameEN ||
      !billerCompanyNameAR ||
      !billerEmail ||
      !billerPhone
    ) {
      toast.error(`❌ ${window.translate("Please fill all required fields")}`);
      return;
    }

    const payload = {
      firstName: { en: billerFirstNameEN, ar: billerFirstNameAR },
      lastName: { en: billerLastNameEN, ar: billerLastNameAR },
      companyName: { en: billerCompanyNameEN, ar: billerCompanyNameAR },
      email: billerEmail,
      phone: billerPhone,
      phone2: billerPhone2,
      address: { en: billerAddressEN, ar: billerAddressAR },
      city: { en: billerCityEN, ar: billerCityAR },
      state: { en: billerStateEN, ar: billerStateAR },
      country: { en: billerCountryEN, ar: billerCountryAR },
      postalCode: billerPostalCode,
      image: billerImageKey || "", // ✅ send S3 key only
      status: billerStatus ? "active" : "inactive",
    };

    try {
      await createBiller(payload);
      toast.success(` ${window.translate("Biller created successfully")}`);

      // reset minimal fields / close modal
      setBillerImagePreview("");
      setBillerImageKey("");
      document.getElementById("close-add-stock-btn")?.click();
      setTimeout(() => window.location.reload(), 800);
    } catch (err) {
      toast.error(`❌ ${window.translate("Failed to create biller")}`);
    }
  };

  const handleUpdateBiller = async () => {
    try {
      const payload = {
        firstName: { en: editFirstNameEN, ar: editFirstNameAR },
        lastName: { en: editLastNameEN, ar: editLastNameAR },
        companyName: { en: editCompanyNameEN, ar: editCompanyNameAR },
        email: editEmail,
        phone: editPhone,
        phone2: editPhone2,
        address: { en: editAddressEN, ar: editAddressAR },
        city: { en: editCityEN, ar: editCityAR },
        state: { en: editStateEN, ar: editStateAR },
        country: { en: editCountryEN, ar: editCountryAR },
        postalCode: editPostalCode,
        // ✅ if user uploaded a new image in edit, use that S3 key; else keep existing
        image: editImageKey || normalizeKey(editImagePreview || ""),
        status: editStatus ? "active" : "inactive",
      };

      await updateBiller(editBillerId, payload);
      toast.success(` ${window.translate("Biller updated successfully")}`);
      fetchBillers();
      document.getElementById("close-add-stock-btn")?.click();
      setTimeout(() => window.location.reload(), 800);
    } catch (err) {
      toast.error(`❌ ${window.translate("Failed to update biller")}`);
    }
  };

  const handleDeleteBiller = async () => {
    if (!deleteBillerId) {
      toast.error(`❌ ${window.translate("Biller ID not found")}`);
      return;
    }
    try {
      await deleteBillerApi(deleteBillerId);
      toast.success(` ${window.translate("Biller deleted successfully")}`);
      fetchBillers();
      setDeleteBillerId(null);
      document.getElementById("close-add-stock-btn")?.click();
      setTimeout(() => window.location.reload(), 800);
    } catch (error) {
      toast.error(`❌ ${window.translate("Failed to delete biller")}`);
    }
  };

  const fetchBillers = async () => {
    try {
      const data = await getAllBillers();
      setBillers(data || []);
    } catch (err) {
      toast.error(`❌ ${window.translate("Failed to load billers")}`);
    }
  };

  useEffect(() => {
    fetchBillers();
  }, []);

  // ---------------------------------------------------------------------------
  // Table columns
  // ---------------------------------------------------------------------------
  const columns = [
    {
      title: window.translate("Code"),
      dataIndex: "Code",
      sorter: (a, b) => (a.Code || "").length - (b.Code || "").length,
    },
    {
      title: window.translate("Biller"),
      dataIndex: "Biller",
      render: (text, record) => {
        const imgSrc = toPublicUrl(record.image || record.Image || "");
        return (
          <div className="d-flex align-items-center">
            <Link to="#" className="avatar avatar-md me-2 gap-2">
              <img
                src={imgSrc || "/assets/img/users/default-avatar.png"}
                alt="biller"
                className="object-fit-cover h-100 rounded-1 gap-2"
                style={{ width: 40, height: 40, objectFit: "cover" }}
              />
            </Link>
            <Link to="#">{text}</Link>
          </div>
        );
      },
      sorter: (a, b) => (a.Biller || "").length - (b.Biller || "").length,
    },
    {
      title: window.translate("Company Name"),
      dataIndex: "Company_Name",
      sorter: (a, b) => (a.Company_Name || "").length - (b.Company_Name || "").length,
    },
    {
      title: window.translate("Email"),
      dataIndex: "Email",
      sorter: (a, b) => (a.Email || "").length - (b.Email || "").length,
    },
    {
      title: window.translate("Phone"),
      dataIndex: "Phone",
      sorter: (a, b) => (a.Phone || "").length - (b.Phone || "").length,
    },
    {
      title: window.translate("Phone 2"), // ✅ fix duplicate label
      dataIndex: "Phone_2",
      sorter: (a, b) => (a.Phone_2 || "").length - (b.Phone_2 || "").length,
    },
    {
      title: window.translate("Status"),
      dataIndex: "status",
      render: (status) => (
        <span className={`badge ${status === "active" ? "bg-success" : "bg-danger"}`}>
          {(status || "").charAt(0).toUpperCase() + (status || "").slice(1)}
        </span>
      ),
    },
    {
      title: window.translate("Country"),
      dataIndex: "Country",
      sorter: (a, b) => (a.Country || "").length - (b.Country || "").length,
    },
    {
      title: window.translate("Action"),
      dataIndex: "action",
      render: (_, record) => (
        <div className="action-table-data">
          <div className="edit-delete-action">
            <Link
              className="me-2 p-2"
              to="#"
              data-bs-toggle="modal"
              data-bs-target="#edit-units"
              onClick={() => {
                setEditBillerId(record.id);
                setEditFirstNameEN(record.firstName?.en || "");
                setEditFirstNameAR(record.firstName?.ar || "");
                setEditLastNameEN(record.lastName?.en || "");
                setEditLastNameAR(record.lastName?.ar || "");
                setEditCompanyNameEN(record.companyName?.en || "");
                setEditCompanyNameAR(record.companyName?.ar || "");
                setEditEmail(record.Email || "");
                setEditPhone(record.Phone || "");
                setEditPhone2(record.Phone_2 || "");
                setEditAddressEN(record.address?.en || "");
                setEditAddressAR(record.address?.ar || "");
                setEditCityEN(record.city?.en || "");
                setEditCityAR(record.city?.ar || "");
                setEditStateEN(record.state?.en || "");
                setEditStateAR(record.state?.ar || "");
                setEditCountryEN(record.country?.en || "");
                setEditCountryAR(record.country?.ar || "");
                setEditPostalCode(record.postalCode || "");
                setEditStatus(record.status === "active");

                // image existing key/url -> preview + key
                const existing = record.image || record.Image || record.logo || "";
                setEditImageKey(existing ? normalizeKey(existing) : "");
                setEditImagePreview(existing ? toPublicUrl(existing) : "");
              }}
            >
              <Edit className="feather-edit" />
            </Link>

            <Link
              className="confirm-text p-2"
              to="#"
              data-bs-toggle="modal"
              data-bs-target="#delete-modal"
              onClick={() => setDeleteBillerId(record.id)}
            >
              <Trash2 className="feather-trash-2" />
            </Link>
          </div>
        </div>
      ),
      sorter: (a, b) => (a.createdby || "").length - (b.createdby || "").length,
    },
  ];

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="add-item d-flex">
              <div className="page-title">
                <h4 className="fw-bold">{window.translate("Billers")}</h4>
                <h6>{window.translate("Manage your billers")}</h6>
              </div>
            </div>
            <ul className="table-top-head">
              <TooltipIcons />
              <RefreshIcon />
              <CollapesIcon />
            </ul>
            <div className="page-btn">
              <Link
                to="#"
                className="btn btn-primary text-white"
                data-bs-toggle="modal"
                data-bs-target="#add-units"
              >
                <i className="ti ti-circle-plus me-2 ms-2"></i>
                {window.translate("Add Biller")}
              </Link>
            </div>
          </div>

          <div className="card table-list-card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3 gap-1">
              <div className="search-set"></div>
              <div className="d-flex table-dropdown my-xl-auto right-content align-items-center flex-wrap row-gap-3">
                <div className="dropdown me-2">
                  <Link
                    to="#"
                    className="dropdown-toggle btn btn-white btn-md d-inline-flex align-items-center d-none"
                    data-bs-toggle="dropdown"
                  >
                    {window.translate("status")}
                  </Link>
                  <ul className="dropdown-menu  dropdown-menu-end p-3">
                    <li>
                      <Link to="#" className="dropdown-item rounded-1">
                        {window.translate("active")}
                      </Link>
                    </li>
                    <li>
                      <Link to="#" className="dropdown-item rounded-1">
                        In{window.translate("active")}
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="card-body">
              <div className="table-responsive">
                {billers.length > 0 ? (
                  <Table columns={columns} dataSource={billers} />
                ) : (
                  <p className="text-center py-4">
                    {window.translate("No billers found")}.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
        <CommonFooter />
      </div>

      {/* Add biller */}
      <div className="modal fade" id="add-units">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="page-wrapper-new p-0">
              <div className="content">
                <div className="modal-header">
                  <div className="page-title">
                    <h4>{window.translate("Add Biller")}</h4>
                  </div>
                  <button
                    type="button"
                    className="close"
                    data-bs-dismiss="modal"
                    aria-label="Close"
                    onClick={() => {
                      setBillerImagePreview("");
                      setBillerImageKey("");
                    }}
                  >
                    <span aria-hidden="true">×</span>
                  </button>
                </div>

                <div className="modal-body">
                  <form>
                    <div className="new-employee-field">
                      <div className="profile-pic-upload image-field flex gap-2">
                        <div className="profile-pic position-relative p-2 text-center">
                          {billerImagePreview && (
                            <div className="image-preview-container position-relative d-inline-block">
                              <img
                                src={billerImagePreview}
                                alt="Preview"
                                style={{
                                  width: 100,
                                  height: 100,
                                  objectFit: "cover",
                                  borderRadius: 8,
                                  border: "1px solid #ccc",
                                }}
                              />
                              <button
                                type="button"
                                className="btn btn-sm btn-danger position-absolute"
                                style={{
                                  top: -8,
                                  right: -8,
                                  borderRadius: "50%",
                                  padding: "2px 6px",
                                  lineHeight: 1,
                                  fontSize: 12,
                                }}
                                onClick={() => {
                                  setBillerImagePreview("");
                                  setBillerImageKey("");
                                }}
                              >
                                &times;
                              </button>
                            </div>
                          )}
                        </div>

                        <div className="mb-3">
                          <div className="image-upload mb-0">
                            <input
                              type="file"
                              accept="image/*"
                              onChange={handleAddImageChange}
                            />
                            <div className="image-uploads">
                              <h4>{window.translate("uploadImage")}</h4>
                            </div>
                          </div>
                          <p className="mt-2">{window.translate("imageNote")}</p>
                        </div>
                      </div>
                    </div>

                    {/* Form fields */}
                    <div className="row">
                      <div className="col-lg-6 mb-3">
                        <label className="form-label">
                          {window.translate("First Name")} (EN)
                          <span className="text-danger ms-1">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={billerFirstNameEN}
                          onChange={(e) => setBillerFirstNameEN(e.target.value)}
                        />
                      </div>
                      <div className="col-lg-6 mb-3">
                        <label className="form-label">
                          {window.translate("First Name")} (AR)
                          <span className="text-danger ms-1">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={billerFirstNameAR}
                          onChange={(e) => setBillerFirstNameAR(e.target.value)}
                        />
                      </div>

                      <div className="col-lg-6 mb-3">
                        <label className="form-label">
                          {window.translate("Last Name")} (EN)
                          <span className="text-danger ms-1">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={billerLastNameEN}
                          onChange={(e) => setBillerLastNameEN(e.target.value)}
                        />
                      </div>
                      <div className="col-lg-6 mb-3">
                        <label className="form-label">
                          {window.translate("Last Name")} (AR)
                          <span className="text-danger ms-1">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={billerLastNameAR}
                          onChange={(e) => setBillerLastNameAR(e.target.value)}
                        />
                      </div>

                      <div className="col-lg-12 mb-3">
                        <label className="form-label">
                          {window.translate("Company Name")} (EN)
                          <span className="text-danger ms-1">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={billerCompanyNameEN}
                          onChange={(e) => setBillerCompanyNameEN(e.target.value)}
                        />
                      </div>
                      <div className="col-lg-12 mb-3">
                        <label className="form-label">
                          {window.translate("Company Name")} (AR)
                          <span className="text-danger ms-1">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={billerCompanyNameAR}
                          onChange={(e) => setBillerCompanyNameAR(e.target.value)}
                        />
                      </div>

                      <div className="col-lg-12 mb-3">
                        <label className="form-label">
                          {window.translate("Email")}<span className="text-danger ms-1">*</span>
                        </label>
                        <input
                          type="email"
                          className="form-control"
                          value={billerEmail}
                          onChange={(e) => setBillerEmail(e.target.value)}
                        />
                      </div>
                      <div className="col-lg-12 mb-3">
                        <label className="form-label">
                          {window.translate("Phone")}<span className="text-danger ms-1">*</span>
                        </label>
                        <input
                          type="tel"
                          className="form-control"
                          value={billerPhone}
                          onChange={(e) => setBillerPhone(e.target.value)}
                        />
                      </div>
                      <div className="col-lg-12 mb-3">
                        <label className="form-label">
                          {window.translate("Phone")} 2
                        </label>
                        <input
                          type="tel"
                          className="form-control"
                          value={billerPhone2}
                          onChange={(e) => setBillerPhone2(e.target.value)}
                        />
                      </div>

                      <div className="col-lg-12 mb-3">
                        <label className="form-label">
                          {window.translate("Address")} (EN)
                          <span className="text-danger ms-1">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={billerAddressEN}
                          onChange={(e) => setBillerAddressEN(e.target.value)}
                        />
                      </div>
                      <div className="col-lg-12 mb-3">
                        <label className="form-label">
                          {window.translate("Address")} (AR)
                          <span className="text-danger ms-1">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={billerAddressAR}
                          onChange={(e) => setBillerAddressAR(e.target.value)}
                        />
                      </div>

                      <div className="col-lg-6 mb-3">
                        <label className="form-label">
                          {window.translate("City")} (EN)
                          <span className="text-danger ms-1">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={billerCityEN}
                          onChange={(e) => setBillerCityEN(e.target.value)}
                        />
                      </div>
                      <div className="col-lg-6 mb-3">
                        <label className="form-label">
                          {window.translate("City")} (AR)
                          <span className="text-danger ms-1">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={billerCityAR}
                          onChange={(e) => setBillerCityAR(e.target.value)}
                        />
                      </div>

                      <div className="col-lg-6 mb-3">
                        <label className="form-label">
                          {window.translate("State")} (EN)
                          <span className="text-danger ms-1">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={billerStateEN}
                          onChange={(e) => setBillerStateEN(e.target.value)}
                        />
                      </div>
                      <div className="col-lg-6 mb-3">
                        <label className="form-label">
                          {window.translate("State")}  (AR)
                          <span className="text-danger ms-1">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={billerStateAR}
                          onChange={(e) => setBillerStateAR(e.target.value)}
                        />
                      </div>

                      <div className="col-lg-6 mb-3">
                        <label className="form-label">
                          {window.translate("Country")}  (EN)
                          <span className="text-danger ms-1">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={billerCountryEN}
                          onChange={(e) => setBillerCountryEN(e.target.value)}
                        />
                      </div>
                      <div className="col-lg-6 mb-3">
                        <label className="form-label">
                          {window.translate("Country")}  (AR)
                          <span className="text-danger ms-1">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={billerCountryAR}
                          onChange={(e) => setBillerCountryAR(e.target.value)}
                        />
                      </div>

                      <div className="col-lg-6 mb-3">
                        <label className="form-label">
                          {window.translate("Postal Code")} <span className="text-danger ms-1">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={billerPostalCode}
                          onChange={(e) => setBillerPostalCode(e.target.value)}
                        />
                      </div>

                      <div className="col-lg-12">
                        <div className="status-toggle modal-status d-flex justify-content-between align-items-center">
                          <span className="status-label">
                            {window.translate("status")}
                          </span>
                          <input
                            type="checkbox"
                            id="user1"
                            className="check"
                            defaultChecked
                            onChange={(e) => setBillerStatus(e.target.checked)}
                          />
                          <label htmlFor="user1" className="checktoggle"> </label>
                        </div>
                      </div>
                    </div>
                  </form>
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn me-2 btn-secondary fs-13 fw-medium p-2 px-3 shadow-none"
                    data-bs-dismiss="modal"
                  >
                    {window.translate("Cancel")}
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary fs-13 fw-medium p-2 px-3"
                    onClick={handleCreateBiller}
                    data-bs-dismiss="modal"
                  >
                    {window.translate("Add Biller")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Edit biller */}
      <div className="modal fade" id="edit-units">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="page-wrapper-new p-0">
              <div className="content">
                <div className="modal-header">
                  <div className="page-title">
                    <h4>{window.translate("Edit Biller")}</h4>
                  </div>
                  <button
                    type="button"
                    className="close"
                    data-bs-dismiss="modal"
                    aria-label="Close"
                  >
                    <span aria-hidden="true">×</span>
                  </button>
                </div>

                <div className="modal-body">
                  <form>
                    <div className="new-employee-field">
                      <div className="profile-pic-upload image-field flex gap-2">
                        <div className="profile-pic position-relative p-2 text-center">
                          {editImagePreview ? (
                            <div className="image-preview-container position-relative d-inline-block">
                              <img
                                src={editImagePreview}
                                alt="Preview"
                                style={{
                                  width: 100,
                                  height: 100,
                                  objectFit: "cover",
                                  borderRadius: 8,
                                  border: "1px solid #ccc",
                                }}
                              />
                              <button
                                type="button"
                                className="btn btn-sm btn-danger position-absolute"
                                style={{
                                  top: -8,
                                  right: -8,
                                  borderRadius: "50%",
                                  padding: "2px 6px",
                                  lineHeight: 1,
                                  fontSize: 12,
                                }}
                                onClick={() => {
                                  setEditImagePreview("");
                                  setEditImageKey("");
                                }}
                              >
                                &times;
                              </button>
                              <p className="mt-1 text-muted" style={{ fontSize: 12 }}>
                                {window.translate("Preview") || "Preview"}
                              </p>
                            </div>
                          ) : (
                            <span>
                              <i data-feather="plus-circle" className="plus-down-add" />{" "}
                              {window.translate("addImage")}
                            </span>
                          )}
                        </div>

                        <div className="mb-3">
                          <div className="image-upload mb-0">
                            <input
                              type="file"
                              accept="image/*"
                              onChange={handleEditImageChange}
                            />
                            <div className="image-uploads">
                              <h4>{window.translate("uploadImage")}</h4>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Fields */}
                    <div className="row">
                      <div className="col-lg-6 mb-3">
                        <label className="form-label">
                          {window.translate("First Name")} (EN)
                          <span className="text-danger ms-1">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={editFirstNameEN}
                          onChange={(e) => setEditFirstNameEN(e.target.value)}
                        />
                      </div>
                      <div className="col-lg-6 mb-3">
                        <label className="form-label">
                          {window.translate("First Name")} (AR)
                          <span className="text-danger ms-1">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={editFirstNameAR}
                          onChange={(e) => setEditFirstNameAR(e.target.value)}
                        />
                      </div>

                      <div className="col-lg-6 mb-3">
                        <label className="form-label">
                          {window.translate("Last Name")} (EN){" "}
                          <span className="text-danger ms-1">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={editLastNameEN}
                          onChange={(e) => setEditLastNameEN(e.target.value)}
                        />
                      </div>
                      <div className="col-lg-6 mb-3">
                        <label className="form-label">
                          {window.translate("Last Name")} (AR){" "}
                          <span className="text-danger ms-1">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={editLastNameAR}
                          onChange={(e) => setEditLastNameAR(e.target.value)}
                        />
                      </div>

                      <div className="col-lg-12 mb-3">
                        <label className="form-label">
                          {window.translate("Company Name")} (EN)
                          <span className="text-danger ms-1">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={editCompanyNameEN}
                          onChange={(e) => setEditCompanyNameEN(e.target.value)}
                        />
                      </div>
                      <div className="col-lg-12 mb-3">
                        <label className="form-label">
                          {window.translate("Company Name")} (AR)
                          <span className="text-danger ms-1">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={editCompanyNameAR}
                          onChange={(e) => setEditCompanyNameAR(e.target.value)}
                        />
                      </div>

                      <div className="col-lg-12 mb-3">
                        <label className="form-label">
                          {window.translate("Email")}<span className="text-danger ms-1">*</span>
                        </label>
                        <input
                          type="email"
                          className="form-control"
                          value={editEmail}
                          onChange={(e) => setEditEmail(e.target.value)}
                        />
                      </div>
                      <div className="col-lg-12 mb-3">
                        <label className="form-label">
                          {window.translate("Phone")}<span className="text-danger ms-1">*</span>
                        </label>
                        <input
                          type="tel"
                          className="form-control"
                          value={editPhone}
                          onChange={(e) => setEditPhone(e.target.value)}
                        />
                      </div>
                      <div className="col-lg-12 mb-3">
                        <label className="form-label">
                          {window.translate("Phone")} 2
                        </label>
                        <input
                          type="tel"
                          className="form-control"
                          value={editPhone2}
                          onChange={(e) => setEditPhone2(e.target.value)}
                        />
                      </div>

                      <div className="col-lg-12 mb-3">
                        <label className="form-label">
                          {window.translate("Address")} (EN)
                          <span className="text-danger ms-1">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={editAddressEN}
                          onChange={(e) => setEditAddressEN(e.target.value)}
                        />
                      </div>
                      <div className="col-lg-12 mb-3">
                        <label className="form-label">
                          {window.translate("Address")} (AR){" "}
                          <span className="text-danger ms-1">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={editAddressAR}
                          onChange={(e) => setEditAddressAR(e.target.value)}
                        />
                      </div>

                      <div className="col-lg-6 mb-3">
                        <label className="form-label">
                          {window.translate("City")} (EN)<span className="text-danger ms-1">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={editCityEN}
                          onChange={(e) => setEditCityEN(e.target.value)}
                        />
                      </div>
                      <div className="col-lg-6 mb-3">
                        <label className="form-label">
                          {window.translate("City")} (AR)<span className="text-danger ms-1">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={editCityAR}
                          onChange={(e) => setEditCityAR(e.target.value)}
                        />
                      </div>

                      <div className="col-lg-6 mb-3">
                        <label className="form-label">
                          {window.translate("State")} (EN)<span className="text-danger ms-1">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={editStateEN}
                          onChange={(e) => setEditStateEN(e.target.value)}
                        />
                      </div>
                      <div className="col-lg-6 mb-3">
                        <label className="form-label">
                          {window.translate("State")} (AR)<span className="text-danger ms-1">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={editStateAR}
                          onChange={(e) => setEditStateAR(e.target.value)}
                        />
                      </div>

                      <div className="col-lg-6 mb-3">
                        <label className="form-label">
                          {window.translate("Country")} (EN)
                          <span className="text-danger ms-1">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={editCountryEN}
                          onChange={(e) => setEditCountryEN(e.target.value)}
                        />
                      </div>
                      <div className="col-lg-6 mb-3">
                        <label className="form-label">
                          {window.translate("Country")} (AR)
                          <span className="text-danger ms-1">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={editCountryAR}
                          onChange={(e) => setEditCountryAR(e.target.value)}
                        />
                      </div>

                      <div className="col-lg-6 mb-3">
                        <label className="form-label">
                          {window.translate("Postal Code")}<span className="text-danger ms-1">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={editPostalCode}
                          onChange={(e) => setEditPostalCode(e.target.value)}
                        />
                      </div>

                      <div className="col-lg-12">
                        <div className="status-toggle modal-status d-flex justify-content-between align-items-center">
                          <span className="status-label">
                            {window.translate("status")}
                          </span>
                          <input
                            type="checkbox"
                            id="user2"
                            className="check"
                            checked={editStatus}
                            onChange={(e) => setEditStatus(e.target.checked)}
                          />
                          <label htmlFor="user2" className="checktoggle"> </label>
                        </div>
                      </div>
                    </div>
                  </form>
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn me-2 btn-secondary fs-13 fw-medium p-2 px-3 shadow-none"
                    data-bs-dismiss="modal"
                  >
                    {window.translate("Cancel")}
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary fs-13 fw-medium p-2 px-3"
                    onClick={handleUpdateBiller}
                    data-bs-dismiss="modal"
                  >
                    {window.translate("Edit Biller")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* delete modal */}
      <div className="modal fade" id="delete-modal">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="page-wrapper-new p-0">
              <div className="content p-5 px-3 text-center">
                <span className="rounded-circle d-inline-flex p-2 bg-danger-transparent mb-2">
                  <i className="ti ti-trash fs-24 text-danger" />
                </span>
                <h4 className="fs-20 fw-bold mb-2 mt-1">{window.translate("Delete")}</h4>
                <p className="mb-0 fs-16">
                  {window.translate("Are you sure you want to delete biller")}
                </p>
                <div className="modal-footer-btn mt-3 d-flex justify-content-center">
                  <button
                    type="button"
                    className="btn me-2 btn-secondary fs-13 fw-medium p-2 px-3 shadow-none"
                    data-bs-dismiss="modal"
                  >
                    {window.translate("Cancel")}
                  </button>
                  <button
                    to="#"
                    className="btn btn-primary fs-13 fw-medium p-2 px-3"
                    data-bs-dismiss="modal"
                    onClick={handleDeleteBiller}
                  >
                    {window.translate("Yes Delete")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Biller;
